import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import CustomButton from 'lib/designSystem/Button';
import React, { useState } from 'react';

interface DocumentWithCollections {
  documentId: string;
  folio: string;
  businessName: string;
  resellId: number;
  resellNetAmount: number;
  totalConciliated: number;
  pendingAmount: number;
  proposedPayment: number;
  hasExistingCollections: boolean;
  wouldCreateExcess: boolean;
  excessAmount: number;
  conciliationsSummary: {
    fundPayments: number;
    ownCollections: number;
    resellNominees: number;
    totalConciliations: number;
  };
}

interface CollectionsWarningDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  documentsWithWarnings: DocumentWithCollections[];
}

const formatCurrency = (amount: number) => {
  return `$${amount.toLocaleString('es-CL')}`;
};

const CollectionsWarningDialog: React.FC<CollectionsWarningDialogProps> = ({
  open,
  onClose,
  onConfirm,
  documentsWithWarnings,
}) => {
  const excessDocuments = documentsWithWarnings.filter(
    (doc) => doc.wouldCreateExcess,
  );
  const withCollectionsDocuments = documentsWithWarnings.filter(
    (doc) => doc.hasExistingCollections && !doc.wouldCreateExcess,
  );

  const initialExpandedCategory =
    excessDocuments.length > 0 ? 'excess' : 'warning';

  const [expandedCategory, setExpandedCategory] = useState<string | false>(
    initialExpandedCategory,
  );
  const [expandedDocument, setExpandedDocument] = useState<string | false>(
    false,
  );

  const handleCategoryChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedCategory(isExpanded ? panel : false);
      setExpandedDocument(false);
    };

  const handleDocumentChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedDocument(isExpanded ? panel : false);
    };

  const renderDocumentAccordion = (
    doc: DocumentWithCollections,
    index: number,
    isExcess: boolean,
  ) => {
    const panelId = `${isExcess ? 'excess' : 'warning'}-doc-${index}`;

    return (
      <Accordion
        key={doc.documentId}
        expanded={expandedDocument === panelId}
        onChange={handleDocumentChange(panelId)}
        sx={{
          mb: 1,
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'grey.300',
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            margin: '0 0 8px 0',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon fontSize="small" />}
          sx={{
            backgroundColor: 'grey.50',
            minHeight: 48,
            '&.Mui-expanded': {
              minHeight: 48,
            },
            '& .MuiAccordionSummary-content': {
              margin: '8px 0',
            },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ width: '100%' }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                Folio: {doc.folio}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {doc.businessName}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5}>
              {isExcess && (
                <Chip
                  label={`Exceso: ${formatCurrency(doc.excessAmount)}`}
                  color="error"
                  size="small"
                  sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                />
              )}
              <Chip
                label={`Conciliado: ${formatCurrency(doc.totalConciliated)}`}
                color={isExcess ? 'error' : 'warning'}
                variant="outlined"
                size="small"
                sx={{ fontSize: '0.7rem' }}
              />
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, backgroundColor: 'white' }}>
          <Stack spacing={2}>
            {/* Información principal */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: 'block', fontWeight: 600 }}
              >
                Información del documento
              </Typography>
              <Stack spacing={0.75}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Monto neto reventa:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(doc.resellNetAmount)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Total conciliado:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(doc.totalConciliated)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Monto pendiente:
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="success.main"
                  >
                    {formatCurrency(doc.pendingAmount)}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Pago propuesto:
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color={isExcess ? 'error.main' : 'warning.dark'}
                  >
                    {formatCurrency(doc.proposedPayment)}
                  </Typography>
                </Stack>
                {isExcess && (
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{
                      p: 1.5,
                      bgcolor: 'error.lighter',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'error.light',
                      mt: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="error.dark"
                    >
                      Excedente que se creará:
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      color="error.main"
                    >
                      {formatCurrency(doc.excessAmount)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>

            {/* Desglose de conciliaciones */}
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: 'block', fontWeight: 600 }}
              >
                Desglose de conciliaciones existentes
              </Typography>
              <Stack spacing={0.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    Pagos de fondo
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatCurrency(doc.conciliationsSummary.fundPayments)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    Recaudación propia
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatCurrency(doc.conciliationsSummary.ownCollections)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    Pagos por Nómina
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatCurrency(doc.conciliationsSummary.resellNominees)}
                  </Typography>
                </Stack>
                <Divider sx={{ my: 0.5 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="caption" fontWeight={700}>
                    Total conciliaciones
                  </Typography>
                  <Typography variant="caption" fontWeight={700}>
                    {formatCurrency(
                      doc.conciliationsSummary.totalConciliations,
                    )}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="collections-warning-title"
    >
      <DialogTitle id="collections-warning-title">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <WarningAmberIcon color="warning" fontSize="medium" />
            <Typography variant="h6" fontWeight={600}>
              Advertencia: Recaudaciones Existentes
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Revisa la información antes de continuar
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {excessDocuments.length > 0 && (
          <Accordion
            expanded={expandedCategory === 'excess'}
            onChange={handleCategoryChange('excess')}
            sx={{
              mb: 2,
              boxShadow: 'none',
              border: '2px solid',
              borderColor: 'error.main',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: 'error.lighter',
                minHeight: 64,
                '&.Mui-expanded': {
                  minHeight: 64,
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                {/* <ErrorOutlineIcon color="error" fontSize="medium" /> */}
                <Box>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="error.dark"
                  >
                    Documentos que generarían excedentes (
                    {excessDocuments.length})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Los siguientes pagos crearán una cuenta por cobrar al fondo
                  </Typography>
                </Box>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2, backgroundColor: 'grey.50' }}>
              {excessDocuments.map((doc, index) =>
                renderDocumentAccordion(doc, index, true),
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {withCollectionsDocuments.length > 0 && (
          <Accordion
            expanded={expandedCategory === 'warning'}
            onChange={handleCategoryChange('warning')}
            sx={{
              mb: 2,
              boxShadow: 'none',
              border: '2px solid',
              borderColor: 'warning.main',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: 'warning.lighter',
                minHeight: 64,
                '&.Mui-expanded': {
                  minHeight: 64,
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                {/* <InfoOutlinedIcon color="warning" fontSize="medium" /> */}
                <Box>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="warning.dark"
                  >
                    Documentos con recaudaciones previas (
                    {withCollectionsDocuments.length})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Estos documentos ya tienen pagos registrados
                  </Typography>
                </Box>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2, backgroundColor: 'grey.50' }}>
              {withCollectionsDocuments.map((doc, index) =>
                renderDocumentAccordion(doc, index, false),
              )}
            </AccordionDetails>
          </Accordion>
        )}

        <Box
          sx={{
            p: 2,
            bgcolor: 'info.lighter',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'info.light',
            mt: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <InfoOutlinedIcon color="info" />
            <Typography variant="body2" color="text.secondary">
              ¿Deseas continuar con el registro de estos pagos?
            </Typography>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <CustomButton onClick={onClose} color="secondary">
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={onConfirm}
          color={excessDocuments.length > 0 ? 'error' : 'warning'}
        >
          Continuar de todas formas
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default CollectionsWarningDialog;
